import React, { RefObject, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'next/link';

import {
  PopoverNavButton,
  PopoverNavItem,
  PopoverNavLink,
  StyledPopover,
  buttonResetStyles,
} from 'src/dispensary/core-menu/components/header/header.styles';

import { EmbeddedAccountIcon } from 'src/assets/embedded-account-icon';
import { useUserButton } from './use-user-button';

type UserButtonProps = {
  onRef?: (ref: React.RefObject<HTMLButtonElement>) => void;
};

export const UserButton = ({ onRef }: UserButtonProps = {}): JSX.Element | null => {
  const {
    isUserLoggedIn,
    firstName,
    isPopoverOpen,
    popoverTriggerRef,
    setIsPopoverOpen,
    loggedInRoutes,
    loyaltyPoints,
    handleLogout,
    handleLogin,
    handlePopoverLinkClick,
    handlePopoverToggle,
  } = useUserButton();

  const hasLoyaltyPoints = Number.isInteger(Number.parseInt(loyaltyPoints, 10));

  useEffect(() => {
    if (onRef) {
      onRef(popoverTriggerRef);
    }
  }, [onRef, popoverTriggerRef]);

  return (
    <>
      <Button
        onClick={isUserLoggedIn ? () => handlePopoverToggle('Account Button') : handleLogin}
        ref={popoverTriggerRef}
        data-testid='user-button'
      >
        <EmbeddedAccountIcon fill='currentColor' />
        {isUserLoggedIn ? (
          <div>
            <FirstName>{firstName}</FirstName> {hasLoyaltyPoints ? `${loyaltyPoints} pts` : 'Account'}
          </div>
        ) : (
          'Login'
        )}
      </Button>

      {isUserLoggedIn && (
        <StyledPopover
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={popoverTriggerRef.current}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          data-testid='user-button-popover'
          $offsetTop
        >
          <nav>
            <>
              <PopoverFirstName>Hi, {firstName}</PopoverFirstName>
              {loggedInRoutes.map(
                ({ href, label, hide }) =>
                  !hide && (
                    <Link href={href} key={label}>
                      <PopoverNavLink href={href} onClick={() => handlePopoverLinkClick(label)}>
                        {label}
                      </PopoverNavLink>
                    </Link>
                  )
              )}
              <PopoverNavButton onClick={handleLogout}>Logout</PopoverNavButton>
            </>
          </nav>
        </StyledPopover>
      )}
    </>
  );
};

const Button = styled.button`
  ${buttonResetStyles}

  align-items: center;
  color: currentColor;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  justify-content: space-between;
  line-height: ${18 / 13};

  @media (min-width: 880px) {
    gap: 12px;
  }
`;

const PopoverFirstName = styled(PopoverNavItem)`
  border-bottom: 1px solid #d9d9d9;
  margin: 0 24px;
  padding-left: 0;
  padding-right: 0;
  width: auto;

  @media (min-width: 880px) {
    display: none;
  }
`;

const FirstName = styled.div`
  color: #a0a7ab;
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 879px) {
    display: none;
  }
`;
